/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { usePostRequest } from "../../../../../../../api/helper"
import { ARGOS_PENDING_COLOR, ARGOS_PROGRESS_COLOR } from "../../../../../../../constants/colors"
import VAR_COMMON_CONSTANTS from "../../../../../../../constants/globalVariables"
import { theme } from "../../../../../../../constants/theme"
import {
    CoverageRequirementsRedlineTreeDbItemUpdateActionType,
    editDatasetInExecutionStatusMap,
    selectDatasetsExecutionStatus,
    selectStudyIdToTreeTypeToTree,
    setStudyIdToTreeTypeToTree,
    setTriggerFetchDatasetsData,
    updateDatasetsExecutionStatus
} from "../../../../../../../redux/ducks/CoverageRequirements/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks"
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox"
import Loader from "../../../../../../components/Loader/Loader"
import useGetHubName from "../../../../../../components/Socket/useGetHubName"
import useSocket from "../../../../../../components/Socket/useSocket"
import { HorizontalTabs } from "../../../../../../components/Tabs/HorizontalTabs"
import {
    COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_INDEX_URL_PARAM,
    EDatasetExecutionStatus,
    IN_PROGRESS_STATUSES,
    MERGED_DATASET_TREE_TYPE,
    refreshTreeWithSanityCheck,
    SINGLE_DATASET_TREE_TYPE
} from "../../../constants"
import { getCoverageRequirementsSelectedDatasetId } from "../CoverageRequirements"
import CoverageRequirementsTreeComponent, { ITreeNode } from "./TreeComponent/CoverageRequirementsTreeComponent"
import { inflate } from 'pako';
import base64js from 'base64-js';

interface ILeftPanelProps {
  studyId: string
  studyName: string
  isPlanOfRecord?: boolean
  setDatasetPath: any
  setDatasetChange: any
  datasetChange: any
  onClickAbort: any
  setSeed: any
  fetchStudy:any
  isReadOnlyStudy?: boolean
}

export const enum ERawMergedDatasetsTreesHorizontalTabNames {
  RAW = "Raw Datasets",
  MERGED = "Merged Datasets"
}

const LeftPanelCoverageRequirements = (props: ILeftPanelProps) => {

  /**
   * Execution status tracking
   */
  const datasetExecutionStatusMap = useAppSelector(selectDatasetsExecutionStatus)
  const mapRef = useRef<any>(datasetExecutionStatusMap)
  mapRef.current = datasetExecutionStatusMap
  const dispatch = useAppDispatch()
  const selectedDatasetId = getCoverageRequirementsSelectedDatasetId()

  /**
   * Tree data
   */
  const rawRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, SINGLE_DATASET_TREE_TYPE, props.studyId))
  const mergedRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, MERGED_DATASET_TREE_TYPE, props.studyId))

  /**
   * Poll for updates to raw dataset tree (pulls updates from the datasets container).
   */
  const refreshRawTreeHandle = usePostRequest(refreshTreeWithSanityCheck)

  useEffect(() => {
    handleRefreshRawTreeHandle(props.studyId)
  }, [props.studyId]);

  /**
   * Socket for tree updates.
   */
  const onMessage = useCallback((msg: any) => {
    // The only case where it is a string should be when we compress and encode it
    if (typeof msg === 'string') {
      msg = decodeAndDecompress(msg)
    }
    const data = msg.data
    const type = msg.type

    data.studyId = props.studyId

    switch (type) {
      case CoverageRequirementsRedlineTreeDbItemUpdateActionType.ExecutionStatusUpdate:
        data.treeType = SINGLE_DATASET_TREE_TYPE
        handleExecutionStatusUpdate(data, selectedDatasetId, dispatch)
        break
      case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateTreeWithoutExecutionStatus:
        data.treeType = SINGLE_DATASET_TREE_TYPE
        handleUpdateTreeWithoutExecutionStatus(dispatch, data)
        break
      case CoverageRequirementsRedlineTreeDbItemUpdateActionType.MergedTreeExecutionStatusUpdate:
        data.treeType = MERGED_DATASET_TREE_TYPE
        handleMergedExecutionStatusUpdate(data, selectedDatasetId, dispatch)
        break
      case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateMergedTreeWithoutExecutionStatus:
        data.treeType = MERGED_DATASET_TREE_TYPE
        handleUpdateMergedTreeWithoutExecutionStatus(dispatch, data)
        break
      case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateNode:
        const {nodeIdToData, treeType, studyId} = data
        if (treeType == SINGLE_DATASET_TREE_TYPE) {
          dispatch(setStudyIdToTreeTypeToTree({
            actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateNode,
            nodeIdToData,
            studyId,
            treeType
          }))
        } else {
          dispatch(setStudyIdToTreeTypeToTree({
            actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateNode,
            nodeIdToData,
            studyId,
            treeType
          }))
        }
        break
      default:
        break
    }

  }, [selectedDatasetId, props.studyId])
  const hubName = useGetHubName(`coverageRequirementsTree_${props.studyId}`)
  useSocket(hubName, onMessage)

  const handleRefreshRawTreeHandle = (studyId: string) => {
    let body: any = {"studyId": studyId}
    refreshRawTreeHandle(body).then((data) => {
      if (data == null) return
      const _rawRedlineTreeDbItem = data?.["single_tree"]
      const singleDatasetsTree = _rawRedlineTreeDbItem?.tree
      const _mergedRedlineTreeDbItem = data?.["merged_tree"]
      const mergedDatasetsTree = _mergedRedlineTreeDbItem?.tree
      const updatedExecutionStatusMap = updateReduxExecutionStatus(singleDatasetsTree)
      updateReduxExecutionStatus(mergedDatasetsTree)

      /**
       * Get the execution status of the selected dataset, and compare it to the singleDatasetsTree, and
       * if the status changes to complete, then we need to trigger an update to the coverageRequirementsData
       * in redux (so that the dataset results are up-to-date in redux, because that's what the right panel uses for results data).
       */
      if (selectedDatasetId != null && updatedExecutionStatusMap != null) {
        const oldExecutionStatus = mapRef.current?.[selectedDatasetId]
        const newExecutionStatus = updatedExecutionStatusMap?.[selectedDatasetId]
        if(oldExecutionStatus != newExecutionStatus && newExecutionStatus == EDatasetExecutionStatus.COMPLETED){
          dispatch(setTriggerFetchDatasetsData())
        }
        else if(IN_PROGRESS_STATUSES.has(oldExecutionStatus) && newExecutionStatus == EDatasetExecutionStatus.ABORTED) {
          dispatch(setTriggerFetchDatasetsData())
        }
      }
      /**
       * TODO: When the execution status of a dataset changes, we should update the tree to reflect that. We can create a
       * callback here that takes the datasetId and the new status, and then updates the tree accordingly.
       */
      dispatch(setStudyIdToTreeTypeToTree({
        actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.FullTreeDbItem,
        treeDbItem: _rawRedlineTreeDbItem,
        treeType: SINGLE_DATASET_TREE_TYPE,
        studyId
      }))
      dispatch(setStudyIdToTreeTypeToTree({
        actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.FullTreeDbItem,
        treeDbItem: _mergedRedlineTreeDbItem,
        treeType: MERGED_DATASET_TREE_TYPE,
        studyId
      }))
    }).catch((e) => {
      console.log('Error fetching tree data', e)
    })
  }
  const updateReduxExecutionStatus = (treeNodes: ITreeNode) => {
    let localExecutionStatusMap: any = {}
    updateDatasetExecutionStatusMap(treeNodes, localExecutionStatusMap)
    dispatch(updateDatasetsExecutionStatus(localExecutionStatusMap))
    return localExecutionStatusMap
  }

  const updateDatasetExecutionStatusMap = (tree: ITreeNode, localExecutionStatusMap: any) => {
    // console.log('tree', tree)
    if (tree == null) return
    if (tree?.nodeType == "dataset") {
      if (tree.datasetId != undefined) {
        // console.log('tree.datasetId', tree.datasetId, tree.executionStatus)
        localExecutionStatusMap[tree.datasetId] = tree.executionStatus
      }
      return
    } else {
      tree?.children?.forEach((child: ITreeNode) => {
        updateDatasetExecutionStatusMap(child, localExecutionStatusMap)
      })
    }
    return
  }
  /**
   * Create tree tabs when tree data changes, which right now will be every time we poll.
   */
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [leftSidebarTabs, setLeftSidebarTabs] = useState<any[]>([])
  useEffect(() => {
    if (rawRedlineTreeDbItem == null || mergedRedlineTreeDbItem == null) return
    setIsLoading(false)
    renderTreeTabs(rawRedlineTreeDbItem, mergedRedlineTreeDbItem)
  }, [rawRedlineTreeDbItem, mergedRedlineTreeDbItem, props?.isReadOnlyStudy])

  const renderTreeTabs = (_rawDatasetsTreeData: any, _mergedDatasetsTreeData: any) => {
    const rawDatasetsTabContent = {
      'name': "Raw " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS,
      'content': <CoverageRequirementsTreeComponent
        setSeed={props.setSeed}
        setDatasetChange={props?.setDatasetChange}
        datasetChange={props?.datasetChange}
        treeObjectData={rawRedlineTreeDbItem}
        treeType={SINGLE_DATASET_TREE_TYPE} studyId={props?.studyId}
        studyName={props?.studyName}
        isPlanOfRecord={props.isPlanOfRecord}
        setDatasetPath={props.setDatasetPath}
        onClickAbort={props?.onClickAbort}
        fetchStudy={props.fetchStudy}
        isReadOnlyStudy={props?.isReadOnlyStudy}
        key={'rawTree'}
      />
    }
    const mergedDataSetsTab = {
      'name': "Merged " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS,
      'content': <CoverageRequirementsTreeComponent
        setDatasetChange={null}
        datasetChange={null}
        treeObjectData={mergedRedlineTreeDbItem}
        treeType={MERGED_DATASET_TREE_TYPE}
        studyId={props?.studyId}
        studyName={props?.studyName}
        isPlanOfRecord={props.isPlanOfRecord}
        setDatasetPath={props.setDatasetPath}
        fetchStudy={props.fetchStudy}
        key={'mergedTree'}
        isReadOnlyStudy={props?.isReadOnlyStudy}
      />
    }

    let localTabsList = []
    localTabsList.push(rawDatasetsTabContent)
    localTabsList.push(mergedDataSetsTab)
    setLeftSidebarTabs(localTabsList)
  }

  return <>
  <Loader isLoading={isLoading}>
    <ArgosFlexbox vertical style={{
      width: '100%',
      overflow: "hidden",
      height: "100%"
    }}>

      <HorizontalTabs
        uid={COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_INDEX_URL_PARAM}
        initContent={null}
        initHtabIndex={0}
        horizontalTabs={leftSidebarTabs} align={'left'}
        outerDivProps={{margin: "5px 0px 5px 0px"}}
        padding={{padding: "0px"}}
        contentDivProps={{margin: "0px"}}
      />

      {/* Legend */}
      <ArgosFlexbox style={{
        borderTop: "2px solid",
        borderTopColor: theme.layout.header.backgroundColor,
        justifyContent: "space-evenly",
        width: "100%",
        padding: '10px'
      }}>
        <ArgosFlexbox vertical>
          <span style={{color: ARGOS_PENDING_COLOR, fontWeight: 'bold', fontSize: 15}}>Pending</span>
          <span style={{color: ARGOS_PROGRESS_COLOR, fontWeight: 'bold', fontSize: 15}}>In Progress</span>
        </ArgosFlexbox>
        <ArgosFlexbox vertical>
          <span style={{color: 'red', fontWeight: 'bold', fontSize: 15}}>Aborted</span>
          <span style={{color: 'green', fontWeight: 'bold', fontSize: 15}}>Completed</span>
        </ArgosFlexbox>
        <ArgosFlexbox vertical>
          <span style={{color: 'orange', fontWeight: 'bold', fontSize: 15}}>Invalid</span>
          <span style={{color: 'black', fontWeight: 'bold', fontSize: 15}}>Not Started</span>
        </ArgosFlexbox>
      </ArgosFlexbox>

    </ArgosFlexbox>
    </Loader>
  </>

}

export function decodeAndDecompress(encodedData: string) {
  // Decode Base64
  const decodedData = base64js.toByteArray(encodedData);

  // Decompress data
  const decompressedData = inflate(decodedData, { to: 'string' });

  // Parse JSON string back to object
  return JSON.parse(decompressedData);
}

const handleUpdateMergedTreeWithoutExecutionStatus = (dispatch: any, data: any) => {
  dispatch(setStudyIdToTreeTypeToTree({
    actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateTreeWithoutExecutionStatus,
    data
  }))
}

export const handleMergedExecutionStatusUpdate = (data: any, selectedDatasetId: string, dispatch: any) => {
  const {datasetId, executionStatus, treeType, studyId} = data
  dispatch(editDatasetInExecutionStatusMap({datasetId, executionStatus}))
  const mapDatasetIdToData = {
    [datasetId]: {
      datasetId: datasetId,
      executionStatus: executionStatus
    }
  }
  dispatch(setStudyIdToTreeTypeToTree({
    actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.ExecutionStatusUpdate,
    mapDatasetIdToData,
    selectedDatasetId,
    treeType,
    studyId
  }))
}

const handleUpdateTreeWithoutExecutionStatus = (dispatch: any, data: any) => {
  dispatch(setStudyIdToTreeTypeToTree({
    actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateTreeWithoutExecutionStatus,
    data
  }))
}
export const handleExecutionStatusUpdate = (data: any, selectedDatasetId: string, dispatch: any) => {
  const {datasetId, executionStatus, treeType, studyId} = data
  dispatch(editDatasetInExecutionStatusMap({datasetId, executionStatus}))
  const mapDatasetIdToData = {
    [datasetId]: {
      datasetId,
      executionStatus
    }
  }
  dispatch(setStudyIdToTreeTypeToTree({
    actionType: CoverageRequirementsRedlineTreeDbItemUpdateActionType.ExecutionStatusUpdate,
    mapDatasetIdToData,
    selectedDatasetId,
    treeType,
    studyId
  }))
}

export default LeftPanelCoverageRequirements